import { useCallback, useEffect, useMemo, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import { getPulseAlerts } from '../../services/pulseService';

const LEVELS = ['', 'Warm', 'Busy', 'Hot', 'Critical'];
const LOCATION_TYPES = ['', 'Airport', 'Downtown', 'Custom', 'Area'];
const STATUSES = ['', 'active', 'expired', 'retired'];

const fmtInt = (value) => Number(value || 0).toLocaleString();

const fmtDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

function today() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

const PRESETS = [
  { label: 'Last 7 days', from: daysAgo(6), to: today() },
  { label: 'Last 30 days', from: daysAgo(29), to: today() },
  { label: 'Last 90 days', from: daysAgo(89), to: today() },
];

function SummaryCard({ label, value, accent }) {
  return (
    <div className="panel" style={{ padding: '18px 20px' }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 900, color: accent ? 'var(--accent)' : 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

const PulseHistoryReport = () => {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(today());
  const [level, setLevel] = useState('');
  const [locationType, setLocationType] = useState('');
  const [status, setStatus] = useState('');
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(1);
  const [alerts, setAlerts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit, from, to };
      if (level) params.level = level;
      if (locationType) params.locationType = locationType;
      if (status) params.status = status;
      const response = await getPulseAlerts(params);
      setAlerts(response.data?.alerts || []);
      setPagination(response.data?.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load Pulse history.');
    } finally {
      setLoading(false);
    }
  }, [from, level, limit, locationType, page, status, to]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const summary = useMemo(() => {
    return alerts.reduce(
      (acc, alert) => {
        acc.pickups += Number(alert.pickupCount || 0);
        acc.targeted += Number(alert.driversTargeted || 0);
        acc.fcmSent += Number(alert.driversNotifiedFcm || 0);
        acc.fcmFailed += Number(alert.driversFailedFcm || 0);
        if (alert.status === 'active') acc.active += 1;
        return acc;
      },
      { pickups: 0, targeted: 0, fcmSent: 0, fcmFailed: 0, active: 0 },
    );
  }, [alerts]);

  const applyPreset = (preset) => {
    setFrom(preset.from);
    setTo(preset.to);
    setPage(1);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    loadAlerts();
  };

  return (
    <AppLayout title="Pulse History" subtitle="Audit trail of TaxiOps Pulse demand alerts sent to the fleet.">
      <div className="surface" style={{ marginBottom: 24 }}>
        <form onSubmit={handleSubmit}>
          <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            {PRESETS.map((preset) => (
              <button key={preset.label} type="button" className="btn btn-ghost" style={{ fontSize: '0.8rem' }} onClick={() => applyPreset(preset)}>
                {preset.label}
              </button>
            ))}
          </div>

          <div className="form-grid">
            <div>
              <label htmlFor="pulse-from">From</label>
              <input id="pulse-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
            </div>
            <div>
              <label htmlFor="pulse-to">To</label>
              <input id="pulse-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
            </div>
            <div>
              <label htmlFor="pulse-level">Level</label>
              <select id="pulse-level" value={level} onChange={(event) => { setLevel(event.target.value); setPage(1); }}>
                {LEVELS.map((item) => <option key={item || 'all'} value={item}>{item || 'All levels'}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="pulse-location">Location</label>
              <select id="pulse-location" value={locationType} onChange={(event) => { setLocationType(event.target.value); setPage(1); }}>
                {LOCATION_TYPES.map((item) => <option key={item || 'all'} value={item}>{item || 'All locations'}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="pulse-status">Status</label>
              <select id="pulse-status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
                {STATUSES.map((item) => <option key={item || 'all'} value={item}>{item || 'All statuses'}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="pulse-limit">Limit</label>
              <input id="pulse-limit" type="number" min="1" max="200" value={limit} onChange={(event) => { setLimit(Number(event.target.value || 50)); setPage(1); }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Loading...' : 'Run report'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {error ? <div className="feedback error" style={{ marginBottom: 16 }}>{error}</div> : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        <SummaryCard label="Records loaded" value={fmtInt(alerts.length)} accent />
        <SummaryCard label="Total matching" value={fmtInt(pagination.total)} />
        <SummaryCard label="Active pulses" value={fmtInt(summary.active)} />
        <SummaryCard label="Pickup count" value={fmtInt(summary.pickups)} />
        <SummaryCard label="Drivers targeted" value={fmtInt(summary.targeted)} />
        <SummaryCard label="FCM failed" value={fmtInt(summary.fcmFailed)} />
      </div>

      <div className="surface">
        <div className="reports-results-toolbar">
          <div>
            <h3>Pulse alert records</h3>
            <p>Shows what was sent, why it triggered, and delivery results.</p>
          </div>
          <div className="toolbar" style={{ gap: 8 }}>
            <button type="button" className="btn btn-subtle" disabled={loading || page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</button>
            <span className="toolbar summary">Page {pagination.page || page} of {pagination.pages || 1}</span>
            <button type="button" className="btn btn-subtle" disabled={loading || page >= (pagination.pages || 1)} onClick={() => setPage((value) => value + 1)}>Next</button>
          </div>
        </div>

        {loading ? (
          <div className="skeleton" style={{ height: 180 }} />
        ) : alerts.length === 0 ? (
          <div className="empty-state">No Pulse alerts found for the selected filters.</div>
        ) : (
          <div className="table-responsive-stack">
            <table className="data-table compact">
              <thead>
                <tr>
                  <th>Triggered</th>
                  <th>Pulse</th>
                  <th>Demand</th>
                  <th>Delivery</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert) => (
                  <tr key={alert._id}>
                    <td data-label="Triggered">
                      <div className="table-stack">
                        <span className="primary">{fmtDateTime(alert.triggeredAt)}</span>
                        <span className="secondary">Expires {fmtDateTime(alert.expiresAt)}</span>
                      </div>
                    </td>
                    <td data-label="Pulse">
                      <div className="table-stack">
                        <span className="primary">{alert.alertTitle}</span>
                        <span className="secondary">
                          {alert.alertLevel} - {alert.locationType} - {alert.locationLabel || alert.zoneName || 'service area'}
                        </span>
                        {alert.placeName ? <span className="secondary">Audit location: {alert.placeName}</span> : null}
                      </div>
                    </td>
                    <td data-label="Demand">
                      <div className="table-stack">
                        <span className="primary">{fmtInt(alert.pickupCount)} pickups</span>
                        <span className="secondary">{alert.detectionRadiusMiles} mi - {alert.timeWindowMinutes} min</span>
                      </div>
                    </td>
                    <td data-label="Delivery">
                      <div className="table-stack">
                        <span className="primary">{fmtInt(alert.driversTargeted)} targeted</span>
                        <span className="secondary">
                          FCM {fmtInt(alert.driversNotifiedFcm)} sent / {fmtInt(alert.driversFailedFcm)} failed
                        </span>
                        <span className="secondary">Socket {fmtInt(alert.driversNotifiedSocket)}</span>
                      </div>
                    </td>
                    <td data-label="Reason" style={{ maxWidth: 320 }}>
                      <div className="table-stack">
                        <span className="primary">{alert.triggerReason || '-'}</span>
                        <span className="secondary">{alert.deliveryResult || alert.alertMessage || '-'}</span>
                      </div>
                    </td>
                    <td data-label="Status">
                      <span className={`badge ${alert.status === 'active' ? 'badge-success' : 'badge-info'}`}>{alert.status}</span>
                      {alert.retiredReason ? <span className="secondary">{alert.retiredReason}</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default PulseHistoryReport;
